import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import type { Integrator } from "../../store/reseller-admin/integratorsStore";
import type { Provider } from "../../store/reseller-admin/providersStore";
import type { License } from "../../store/reseller-admin/licensesStore";
import type { Brand } from "../../Layouts/reseller-admin/components/sections/models/types";

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

export interface IntegratorListParams {
  page?: number;
  limit?: number;
  name?: string;
  protocol?: string;
  active?: string;
}

interface IntegratorListBody {
  data?: Integrator[];
  meta?: ListMeta;
}

export interface IntegratorListResult {
  data: Integrator[];
  meta?: ListMeta;
}

function clean(params: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v as string | number;
  }
  return out;
}

export function useIntegratorsList(params: IntegratorListParams = {}) {
  return useQuery({
    queryKey: qk.reseller.integrators(params),
    queryFn: ({ signal }) =>
      getJson<IntegratorListBody>(
        "/integratorList",
        clean({ page: 1, limit: 8, ...params }),
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

export interface ProviderListParams {
  page?: number;
  limit?: number;
  name?: string;
  adapter?: string;
  active?: string;
}

interface ProviderListBody {
  data?: Provider[];
  meta?: ListMeta;
}

export interface ProviderListResult {
  data: Provider[];
  meta?: ListMeta;
}

export function useProvidersList(params: ProviderListParams = {}) {
  return useQuery({
    queryKey: qk.reseller.providers(params),
    queryFn: ({ signal }) =>
      getJson<ProviderListBody>(
        "/providerList",
        clean({ page: 1, limit: 8, ...params }),
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

export interface LicenseListParams {
  page?: number;
  limit?: number;
  name?: string;
  status?: string;
  billing_model?: string;
}

interface LicenseListBody {
  data?: License[];
  meta?: ListMeta;
}

export interface LicenseListResult {
  data: License[];
  meta?: ListMeta;
}

export function useLicensesList(params: LicenseListParams = {}) {
  return useQuery({
    queryKey: qk.reseller.licenses(params),
    queryFn: ({ signal }) =>
      getJson<LicenseListBody>(
        "/licenseList",
        clean({ page: 1, limit: 10, ...params }),
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

export interface BrandListParams {
  page?: number;
  limit?: number;
  search?: string;
  brand?: string;
  model?: string;
  protocol?: string;
}

export interface BrandListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface BrandListBody {
  data?: Brand[];
  meta?: {
    currentPage?: number;
    totalItems?: number;
    totalPages?: number;
  };
}

export interface BrandListResult {
  data: Brand[];
  meta: BrandListMeta | null;
}

export function useBrandsList(params: BrandListParams = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.reseller.models({ companyId, ...params }),
    queryFn: ({ signal }) =>
      getJson<BrandListBody>(
        "/brandsByCompany",
        clean({ page: 1, limit: 6, ...params }),
        { signal },
      ).then((body): BrandListResult => ({
        data: body.data ?? [],
        meta: body.meta
          ? {
              page: body.meta.currentPage || 1,
              limit: params.limit || 20,
              total: body.meta.totalItems || 0,
              totalPages: body.meta.totalPages || 1,
            }
          : null,
      })),
  });
}
